import difference from 'lodash-es/difference';
import intersection from 'lodash-es/intersection';
import kebabCase from 'lodash-es/kebabCase';
import pull from 'lodash-es/pull';

import { Badge, BrandResource, SponsorshipResource } from '@/clients/unifiedApi';
import { BADGE_KEYS, STATUSES } from '@/constants';
import { Campaign } from '@/models/campaign';
import { SegmentAdTrackingData } from '@/models/segment';
import { ShopBrand, ShopBrandApiResponse } from '@/models/shop';
import { SponsorshipResponse } from '@/models/sponsorship';
import { PublicMenuBrand } from '@/services/api/models/publicMenu';
import { normalizeDate } from '@/utils/dates';
import sortItems from '@/utils/sortItems';

// hardcoding these company ids so all of its ancillary brands are shown while non-preferred ancillary brands don't show;
// this is a temporary solution to accommodate a company that partnered with leaflink
const ANCILLARY_PREFERRED_BRAND_COMPANY_IDS = [
  3592, // LuvBuds
];

export const cleanBrand = (brand: ShopBrandApiResponse): ShopBrand => {
  return {
    ...brand,
    badges: parseBrandBadges(brand),
    lastOrderDate: brand.lastOrderDate ? new Date(brand.lastOrderDate) : undefined,
    newUntil: brand.newUntil ? new Date(brand.newUntil) : undefined,
    createdOn: brand.createdOn ? new Date(brand.createdOn) : undefined,
    lastUpdate: brand.lastUpdate ? new Date(brand.lastUpdate) : undefined,
    isNonPreferredAncillaryBrand:
      brand.categories?.length === 1 &&
      brand.categories[0] === 'Accessories' &&
      !brand.sponsored &&
      !ANCILLARY_PREFERRED_BRAND_COMPANY_IDS.includes(brand.company.id),
  };
};

export const determineCampaignStatus = (campaign: Campaign) => {
  const setupStartDate = normalizeDate(campaign.setupStartDate).getTime();
  const startDate = normalizeDate(campaign.startDate).getTime();
  const today = new Date().getTime();

  // Currently, there's no need to check against endDate because
  // we would have gotten a 404 from the API if there was no active campaign
  const isSetupPeriod = today >= setupStartDate;
  const isLive = today >= startDate;

  if (isLive) {
    return STATUSES.CAMPAIGN.LIVE;
  }

  return isSetupPeriod ? STATUSES.CAMPAIGN.SETUP : STATUSES.CAMPAIGN.INACTIVE;
};

/**
 * Some sponsorships may not target a product, but if they do, we will
 * add additional Segment tracking data to it
 */
export const getSponsorshipProductWithSegmentTrackingData = (
  sponsorship: SponsorshipResponse,
  trackingData: SegmentAdTrackingData,
) => {
  if (!sponsorship.product) {
    return null;
  }

  const productWithSegmentData = {
    ...sponsorship.product,
    brand: sponsorship.brand,
    brandTrusted: sponsorship.product.brand.trusted,
    categoryName: sponsorship.product.category.name,
    clickUrl: sponsorship.clickUrl,
    trackingData,
    sponsored: true,
  };

  return productWithSegmentData;
};

export const parseBrandBadges = (
  brand: ShopBrandApiResponse | BrandResource | SponsorshipResource | PublicMenuBrand,
) => {
  // content API sponsored brand content is embedded as a resource
  const brandData = 'resource' in brand ? brand.resource : brand;

  const badgeSort = [
    BADGE_KEYS.ANY_CERTIFIED_SELLER,
    BADGE_KEYS.VERIFIED_SELLER,
    BADGE_KEYS.COMPLIANT_SELLER,
    BADGE_KEYS.PAYMENTS,
    BADGE_KEYS.LOGISTICS,
    BADGE_KEYS.FINANCIAL,
    BADGE_KEYS.ANY_LL_LIST,
  ];
  const sellerSort = Object.keys(STATUSES.SELLER_STATUSES).map(kebabCase);

  const badges = brandData.badges
    // selfIdentify badges are used in a place different from the badge key, that's why we need to remove them
    .filter((badge: Badge | string) => (typeof badge === 'string' ? false : !badge?.self_identify))
    .map((badge: Badge | string) => {
      const rawBadgeValue = typeof badge === 'string' ? badge : badge.code;

      return rawBadgeValue.replace('_', '-');
    });

  // Only include highest-achieved seller status
  const sellerStatus = intersection(sellerSort, badges)[0];

  // Remove lower seller status badge(s)
  pull(badges, ...(sellerStatus ? difference(sellerSort, [sellerStatus]) : []));

  // Convert to objects for display
  const badgeObjects = badges.map((key) => {
    let group;
    let params;

    // Leaflink List Winners - include year for translation
    if (key.indexOf(BADGE_KEYS.ANY_LL_LIST) === 0) {
      group = 'list-winner';
      params = {
        year: key.replace(BADGE_KEYS.ANY_LL_LIST, ''),
      };
    } else if (key === BADGE_KEYS.SOCIAL_EQUITY_VERIFIED) {
      const socialEquityPrograms =
        'social_equity_programs' in brandData ? brandData.social_equity_programs : brandData.socialEquityPrograms;

      params = {
        programs: `<br />${socialEquityPrograms.join('<br />')}`,
      };
    }

    return {
      group: group || key,
      key,
      params,
    };
  });

  // Sort badges
  return sortItems(badgeObjects, {
    sortBy: 'key',
    customSorter: (a, b) => {
      // Find indexes of matching badges in `badgeSort`
      const indexA = badgeSort.findIndex((_a) => a.includes(_a));
      const indexB = badgeSort.findIndex((_b) => b.includes(_b));

      // Sort by `badgeSort` order
      if (indexA > indexB) {
        return 1;
      }

      if (indexA < indexB) {
        return -1;
      }

      // Sort LeafLink List entries by year (descending)
      if (a.includes(BADGE_KEYS.ANY_LL_LIST) && b.includes(BADGE_KEYS.ANY_LL_LIST)) {
        if (a > b) {
          return -1;
        }

        if (a < b) {
          return 1;
        }
      }

      return 0;
    },
  });
};
